import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { authService } from '../api/services/auth.service'
import { useAuth } from '../context/AuthContext'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'

const verifyOtpSchema = z.object({
  email: z.string().email('Enter a valid email'),
  otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit OTP'),
})

type VerifyOtpFormValues = z.infer<typeof verifyOtpSchema>

type VerifyOtpLocationState = {
  email?: string
}

export function VerifyOtpPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setSession } = useAuth()
  const state = location.state as VerifyOtpLocationState | null

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyOtpFormValues>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { email: state?.email ?? '', otp: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      const response = await authService.verifyOtp(values.email, values.otp)
      const session = {
        ...response.data,
        token: response.data.token,
      }
      setSession(session)
      navigate(session.dashboard_path ?? '/login', { replace: true })
    } catch {
      // API errors are toasted globally by the axios interceptor.
    }
  })

  return (
    <AuthLayout
      title="Verify your code"
      description="Enter the 6-digit code sent to your email."
      footer={
        <Link className="text-sm font-semibold text-green-600 hover:text-green-700" to="/login">
          Back to Login
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} autoComplete="off">
        <FormRequiredLegend />
        <div>
          <FieldLabel required htmlFor="verify-email">
            Email
          </FieldLabel>
          <Input
            id="verify-email"
            className="mt-1"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            readOnly={Boolean(state?.email)}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>
        <div>
          <FieldLabel required>OTP</FieldLabel>
          <Input
            className="mt-1 text-center text-lg tracking-[0.3em]"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            {...register('otp')}
          />
          {errors.otp && <p className="mt-1 text-xs text-danger">{errors.otp.message}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? 'Verifying...' : 'Verify & Sign In'}
        </Button>
      </form>
    </AuthLayout>
  )
}
